import type { DesktopConnectionState } from '@/components/desktop-connection-status';
import type { CloudRuntimeProvisioningPhase } from '@/lib/cloud-runtime/cloud-runtime';
import type { CloudConversationStartupPhase } from '@/lib/conversations/cloud-conversation-startup';
import type { AgentStartupPhase } from '@/lib/native-agent/types';

/**
 * The connection pill state shown in the conversation toolbar. Preview always
 * reads "ready"; a selected-but-unprovisioned cloud agent shows "on-demand"
 * (idle) or "starting" (a prompt is waiting to boot the runtime); otherwise the
 * live ACP connection state is surfaced verbatim. Pure so it can be unit tested.
 */
export function desktopConnectionState(input: {
  preview: boolean;
  cloudAgentSelected: boolean;
  cloudRuntimeActive: boolean;
  hasPendingCloudMessage: boolean;
  connectionState: DesktopConnectionState;
}): DesktopConnectionState {
  if (input.preview) return 'ready';
  if (input.cloudAgentSelected && !input.cloudRuntimeActive) {
    return input.hasPendingCloudMessage ? 'starting' : 'on-demand';
  }
  return input.connectionState;
}

/**
 * The cloud-conversation startup phase driving the boot progress panel, or
 * `null` when no cloud boot is in flight (preview, non-cloud, or already
 * running). Cloud repository provisioning wins first, then runtime provisioning
 * while a prompt waits, then the ACP session bring-up mapped to its phase. Pure
 * so the phase ladder can be exhaustively unit tested.
 */
export function resolveCloudStartupPhase(input: {
  preview: boolean;
  cloudAgentSelected: boolean;
  cloudRepositoryBusy: boolean;
  hasPendingCloudMessage: boolean;
  cloudRuntimeActive: boolean;
  sessionActive: boolean;
  cloudProvisioningPhase: CloudRuntimeProvisioningPhase;
  agentStartupPhase: AgentStartupPhase;
}): CloudConversationStartupPhase | null {
  if (input.preview || !input.cloudAgentSelected) return null;
  if (input.cloudRepositoryBusy) return 'repository';
  if (input.hasPendingCloudMessage && !input.cloudRuntimeActive) {
    return input.cloudProvisioningPhase;
  }
  if (input.cloudRuntimeActive && !input.sessionActive) {
    switch (input.agentStartupPhase) {
      case 'initializing':
        return 'initializing';
      case 'opening-session':
        return 'opening-session';
      case 'error':
        return 'error';
      default:
        return 'connecting';
    }
  }
  return null;
}
